(() => {
  'use strict';

  window.CS = window.CS || {};
  window.CS.motion = window.CS.motion || {};

  const state = {
    version: '1.0.0',
    initialised: false,
    observer: null,
    tracked: new WeakSet(),
    reducedMotion: false
  };

  const hasTokens = () => !!(window.CS.motion && window.CS.motion.tokens);
  const getTokens = () => window.CS.motion.tokens;
  const readNumber = (value, fallback = 0) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
  };

  function checkReducedMotion() {
    state.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    return state.reducedMotion;
  }

  function resolveToken(group, name, fallback) {
    const tokens = getTokens();
    return tokens[group] && tokens[group][name] !== undefined ? tokens[group][name] : fallback;
  }

  function resolvePreset(name) {
    const tokens = getTokens();
    return tokens.reveal[name] || tokens.reveal.fadeUp;
  }

  function revealNow(el) {
    el.classList.add('is-cs-visible');
    el.setAttribute('data-cs-motion-state', 'visible');
  }

  function hideAgain(el) {
    if (state.reducedMotion) return;
    el.classList.remove('is-cs-visible');
    el.setAttribute('data-cs-motion-state', 'hidden');
  }

  function applyInitialState(el) {
    const type = el.dataset.csReveal || 'fadeUp';
    const preset = resolvePreset(type);

    el.style.setProperty('--cs-reveal-duration', `${resolveToken('duration', preset.duration, 700)}ms`);
    el.style.setProperty('--cs-reveal-easing', resolveToken('easing', preset.easing, 'cubic-bezier(.22,1,.36,1)'));

    if (preset.distance) {
      el.style.setProperty('--cs-reveal-distance', `${resolveToken('distance', preset.distance, 24)}px`);
    }
    if (preset.blur) {
      el.style.setProperty('--cs-reveal-blur', `${resolveToken('blur', preset.blur, 8)}px`);
    }
    if (preset.scale) {
      el.style.setProperty('--cs-reveal-scale', resolveToken('scale', preset.scale, 0.97));
    }

    const delay = readNumber(el.dataset.csDelay, 0);
    const staggerName = el.dataset.csStaggerToken || 'normal';
    const staggerMs = resolveToken('stagger', staggerName, 90);
    el.style.setProperty('--cs-reveal-delay', `${delay * staggerMs}ms`);

    if (!state.reducedMotion) {
      el.classList.add('cs-motion-ready');
    } else {
      revealNow(el);
    }
  }

  function shouldRepeat(el) {
    return el.dataset.csScroll === 'always';
  }

  function thresholdFor(el) {
    const tokens = getTokens();
    const mode = el.dataset.csThreshold || 'normal';

    if (mode === 'low') return tokens.viewport.thresholdLow;
    if (mode === 'high') return tokens.viewport.thresholdHigh;

    const custom = Number(mode);
    if (Number.isFinite(custom) && custom >= 0 && custom <= 1) return custom;

    return tokens.viewport.thresholdNormal;
  }

  function createObserver() {
    if (!('IntersectionObserver' in window) || state.reducedMotion) return null;

    const tokens = getTokens();

    state.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const el = entry.target;

        if (entry.isIntersecting && entry.intersectionRatio >= thresholdFor(el)) {
          revealNow(el);
          if (!shouldRepeat(el)) state.observer.unobserve(el);
        } else if (shouldRepeat(el)) {
          hideAgain(el);
        }
      });
    }, {
      threshold: [0, tokens.viewport.thresholdLow, tokens.viewport.thresholdNormal, tokens.viewport.thresholdHigh, 1],
      rootMargin: tokens.viewport.rootMarginDefault
    });

    return state.observer;
  }

  function registerElement(el) {
    if (state.tracked.has(el)) return;
    applyInitialState(el);
    state.tracked.add(el);

    if (state.reducedMotion || !state.observer) {
      revealNow(el);
      return;
    }
    state.observer.observe(el);
  }

  function registerStory(container) {
    const storyKey = container.dataset.csStory;
    const story = getTokens().story[storyKey];
    if (!story) return;

    const staggerToken = container.dataset.csStoryStagger || story.stagger || 'normal';

    story.order.forEach((role, index) => {
      const matches = container.querySelectorAll(`[data-cs-role="${role}"]`);
      matches.forEach((el, localIndex) => {
        if (!el.dataset.csReveal) el.dataset.csReveal = 'fadeUp';
        if (!el.dataset.csStaggerToken) el.dataset.csStaggerToken = staggerToken;
        if (!el.dataset.csDelay) el.dataset.csDelay = String(index + localIndex);
        registerElement(el);
      });
    });
  }

  function scan(root = document) {
    if (!hasTokens()) {
      console.warn('[CS Motion] M001 Motion Tokens™ not found. M002 paused.');
      return;
    }

    root.querySelectorAll('[data-cs-story]').forEach(registerStory);
    root.querySelectorAll('[data-cs-reveal]').forEach(registerElement);
  }

  function init() {
    if (!hasTokens()) return false;

    checkReducedMotion();

    if (state.observer) {
      state.observer.disconnect();
      state.observer = null;
    }

    createObserver();
    scan(document);
    state.initialised = true;
    document.documentElement.dataset.csMotionEngine = state.version;

    window.dispatchEvent(new CustomEvent('cs:motion-engine-ready', {
      detail: { version: state.version, reducedMotion: state.reducedMotion }
    }));

    return true;
  }

  function refresh(root = document) {
    checkReducedMotion();
    if (!state.observer && !state.reducedMotion) createObserver();
    scan(root);
  }

  function destroy() {
    if (state.observer) {
      state.observer.disconnect();
      state.observer = null;
    }
    state.initialised = false;
  }

  window.CS.motion.engine = Object.freeze({
    version: state.version,
    init,
    refresh,
    destroy,
    reveal: revealNow,
    scan
  });

  function boot() {
    if (hasTokens()) init();
    else window.addEventListener('cs:motion-tokens-ready', init, { once: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => refresh(event.target));
  document.addEventListener('shopify:section:reorder', () => refresh(document));
  document.addEventListener('shopify:block:select', (event) => refresh(event.target.closest('.shopify-section') || document));

  const reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const onPreferenceChange = () => { destroy(); init(); };

  if (typeof reduceQuery.addEventListener === 'function') {
    reduceQuery.addEventListener('change', onPreferenceChange);
  } else if (typeof reduceQuery.addListener === 'function') {
    reduceQuery.addListener(onPreferenceChange);
  }
})();
